import {
  AddOn,
  AttachmentId,
  CountryCode,
  CustomerType,
  Identity,
  PlanType,
  Relationship,
} from '@/enums';
import { getCountryCode } from '@/helpers/CountryCodeHelper';
import { getProductCode } from '@/helpers/ProductHelper';
import { store } from '@/store';
import {
  AddressData,
  BeneficiaryData,
  CreateProposalRequest,
  GetCustomerProfileFERequest,
  InforcePolicyRequest,
  InsuredCustomerData,
  MailingAddress,
  PersonalDetail,
  PersonalDetailPH,
  ProposerData,
  RetrieveProposalRequest,
} from '@/types/Proposal';

import { SOURCEAPP_ECHANNEL, SOURCEAPP_GOTGAFFILIATE } from '@/constants';
import { AffiliateData } from '@/types/Proposal/AffiliateData';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const createTravelProposal = createAsyncThunk(
  'application/CreateTravelProposal',
  async ({ language }: { language: string }) => {
    const rootState = store.getState();
    const planSummary = rootState.planSummary;
    const affilitator = rootState.affilitator;
    const personalDetail = rootState.personalDetail;

    try {
      if (planSummary.quotationDetail && planSummary.priceBreakdown) {
        const selectedAddonId = [];
        if (planSummary.addonCoverages.adventurousActivities.isSelected)
          selectedAddonId.push(AddOn.Adventurous);
        if (planSummary.addonCoverages.covid19.isSelected)
          selectedAddonId.push(AddOn.Covid);

        const insured: InsuredCustomerData[] = [];
        if (Number(planSummary.quotationDetail.planType) === PlanType.Group) {
          personalDetail.adultDetails.forEach((x, index) => {
            if (index > 0) {
              insured.push(
                getInsured(
                  x,
                  Relationship.Group,
                  planSummary.quotationDetail?.planType,
                  personalDetail.adultDetails[0].mailingAddress,
                ),
              );
            }
          });
        } else {
          personalDetail.adultDetails.forEach((x, index) => {
            if (index > 0) {
              insured.push(
                getInsured(
                  x,
                  Relationship.Spouse,
                  planSummary.quotationDetail?.planType,
                ),
              );
            }
          });
        }
        personalDetail.childDetails.forEach((x) => {
          insured.push(
            getInsured(
              x,
              Relationship.Child,
              planSummary.quotationDetail?.planType,
            ),
          );
        });

        const request: CreateProposalRequest = {
          quotationNo: planSummary.priceBreakdown?.quotationNo,
          productCode: getProductCode(),
          tripType: planSummary.quotationDetail.tripType,
          planType: planSummary.quotationDetail.planType,
          area: planSummary.quotationDetail.areaId,
          planClass: planSummary.planClass as number,
          adultCount: planSummary.quotationDetail.adultCount,
          childCount: planSummary.quotationDetail.childCount,
          startDate: planSummary.quotationDetail.travelStartDate,
          endDate: planSummary.quotationDetail.travelEndDate,
          selectedAddOnId: selectedAddonId,
          checkPremium: planSummary.priceBreakdown.totalPremium,
          isRoundTrip: (
            !!planSummary.flightDetails?.[0].departure.flightNumber &&
            !!planSummary.flightDetails?.[0].return.flightNumber
          ),
          marketingConsent: planSummary.marketingConsent,
          declaration: planSummary.declaration,
          proposer: getProposer(
            personalDetail.adultDetails[0],
            planSummary.quotationDetail.planType,
          ),
          insured,
          departureFlightNumber: planSummary.flightDetails?.[0].departure.flightNumber,
          departureDate: planSummary.flightDetails?.[0].departure.flightDate,
          returnFlightNumber: planSummary.flightDetails?.[0].return.flightNumber,
          returnDate: planSummary.flightDetails?.[0].return.flightDate,
          ...planSummary.bankAccountDetail,
          isPartner: false,
          referral: '',
          language,
          payMode: planSummary.payMode,
          isStaff: affilitator.isStaff,
          countryCode: getCountryCode(),
          clientPfNumber: affilitator.clientPfNumber,
          beneficiary: personalDetail.beneficiaryDetail
            ? getBeneficiary(personalDetail.beneficiaryDetail)
            : undefined,
          sourceAppId: affilitator.agentId != null ? SOURCEAPP_GOTGAFFILIATE: SOURCEAPP_ECHANNEL,
        };
        if (affilitator.agentCode) {
          request.agentCode = affilitator.agentCode;
        }
        if (affilitator.isStaff !== null) {
          request.isStaff = affilitator.isStaff;
        }
        if (affilitator.agentId !== null) {
          const newAffiliate: AffiliateData = {
            affiliateId: affilitator.agentId,
          };

          request.affiliate = newAffiliate;
        }

        const relativeUrl = 'travel/CreateProposal';
        const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
        const response = await axiosInterceptorInstance.post(apiUrl, request);
        return response.data;
      } else {
        throw new Error('Failed to create proposal');
      }
    } catch (error) {
      return error;
    }
  },
);

export const retrieveTravelProposal = createAsyncThunk(
  'application/RetrieveTravelProposal',
  async (request: RetrieveProposalRequest) => {
    try {
      const relativeUrl = 'travel/RetrieveProposal';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      if (response.data.succeeded)
        response.data.data.encodedOrderId = request.encodedOrderId;
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

const getProposer = (data: PersonalDetail, planType: number) => {
  const proposerData: ProposerData = {
    address: populateAddress(data),
    birthDate: data.dob || '',
    email: data.email?.includes('*') ? '' : data.email,
    ethnicity: Number(data.ethnicity),
    gender: data.gender,
    home: '',
    identityNumber: data?.identityNumber,
    identityType: data.identityType,
    isExistingCustomer: data.isExistingCustomer,
    lastName: '',
    mobile: data.mobileNumber.includes('*') ? '' : data.mobileNumber,
    name: data.fullName.includes('*') ? '' : data.fullName,
    nationality: data.nationality ?? 0,
    office: '',
    salutation: '',
    type: CustomerType.Individual,
    ...handlePersonalDetailByCountry(data),
  };

  if (Number(planType) === PlanType.Group) {
    proposerData.accountHolder = data.accountHolder;
    proposerData.accountNo = data.accountNo;
    proposerData.bankId = data.bankId;
  }

  return proposerData;
};

const getInsured = (
  data: PersonalDetail,
  relationshipId: number,
  planType?: number,
  proposerAddress?: MailingAddress,
) => {
  const insured: InsuredCustomerData = {
    type: CustomerType.Individual,
    salutation: '',
    name: data.fullName.includes('*') ? '' : data.fullName,
    lastName: '',
    identityType: data.identityType,
    identityNumber: data.identityNumber?.toString(),
    nationality:
      Number(data.identityType) === Identity.NRIC ? 0 : data.nationality ?? 0,
    email: data.email.includes('*') ? '' : data.email,
    mobile: data.mobileNumber.includes('*') ? '' : data.mobileNumber,
    home: '',
    office: '',
    birthDate: data.dob,
    gender: data.gender,
    ethnicity: Number(data.ethnicity),
    address: populateAddress(data, proposerAddress),
    relationshipId,
    isChild: relationshipId === Relationship.Child,
    isStudent: !!Number(data.isStudent ?? false),
    ...handlePersonalDetailByCountry(data),
  };

  if (Number(planType) === PlanType.Group) {
    insured.accountHolder = data.accountHolder;
    insured.accountNo = data.accountNo;
    insured.bankId = data.bankId;
  }

  return insured;
};

const getBeneficiary = (data: PersonalDetail) => {
  const beneficiaryData: BeneficiaryData = {
    address: populateAddress(data),
    birthDate: data.dob || '',
    gender: data.gender,
    identityNumber: data.identityNumber,
    mobile: data.mobileNumber.includes('*') ? '' : data.mobileNumber,
    name: data.fullName.includes('*') ? '' : data.fullName,
    nationality: data.nationality ?? 0,
    relationship: data.relationship ?? '',
    type: CustomerType.Individual,
    ethnicity: 0,
  };

  return beneficiaryData;
};

const sendEmailStatus = {
  [CountryCode.Malaysia]: true,
  [CountryCode.Philippines]: true,
};

const sendSMSStatus = {
  [CountryCode.Malaysia]: true,
  [CountryCode.Philippines]: false,
};

const hasPolicyPackStatus = {
  [CountryCode.Malaysia]: false,
  [CountryCode.Philippines]: true,
};

export const inforcePolicy = createAsyncThunk(
  'application/InforceProposal',
  async (encodedOrderId: string) => {
    try {
      const countryCode = getCountryCode();
      const request: InforcePolicyRequest = {
        encodedOrderId,
        sendEmail: sendEmailStatus[countryCode],
        sendSMS: sendSMSStatus[countryCode],
        hasPolicyPack: hasPolicyPackStatus[countryCode],
        countryCode: countryCode,
      };
      const relativeUrl = 'InforcePolicy';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getCustomerProfileFE = createAsyncThunk(
  'screening/getCustomerProfileFE',
  async (nric: string) => {
    try {
      const request: GetCustomerProfileFERequest = {
        sourceAppName: 'DSP_TripCareFE',
        icNumber: nric,
      };
      const relativeUrl = 'MDMScreening/GetCustomerProfileFE';
      const apiUrl = `/screening/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

const populateAddress = (
  data: PersonalDetail,
  proposerAddress?: MailingAddress,
): AddressData | undefined => {
  if (proposerAddress !== undefined) {
    return {
      addressLine1: proposerAddress.buildingName.includes('*')
        ? ''
        : proposerAddress.buildingName,
      addressLine2: proposerAddress.areaName.includes('*')
        ? ''
        : proposerAddress.areaName,
      city: proposerAddress.cityName?.includes('*')
        ? ''
        : proposerAddress.cityName,
      postcode: proposerAddress.postcode.includes('*')
        ? ''
        : proposerAddress.postcode,
      state: proposerAddress.state.includes('*') ? '' : proposerAddress.state,
    };
  } else {
    return {
      addressLine1: data.mailingAddress.buildingName.includes('*')
        ? ''
        : data.mailingAddress.buildingName,
      addressLine2: data.mailingAddress.areaName.includes('*')
        ? ''
        : data.mailingAddress.areaName,
      city: data.mailingAddress.cityName?.includes('*')
        ? ''
        : data.mailingAddress.cityName,
      postcode: data.mailingAddress.postcode.includes('*')
        ? ''
        : data.mailingAddress.postcode,
      state: data.mailingAddress.state.includes('*')
        ? ''
        : data.mailingAddress.state,
    };
  }
};

//type guard
const getCountryType = (data: PersonalDetail) => {
  if ((data as PersonalDetailPH).passportImageId) {
    return CountryCode.Philippines;
  }

  return CountryCode.Malaysia;
};

const handlePersonalDetailsPH = (
  data: PersonalDetail,
): Partial<ProposerData | InsuredCustomerData> => {
  const dataPH = data as PersonalDetailPH;

  return {
    dualNationality: dataPH?.dualNationality,
    secondaryIdentityType: dataPH?.secondaryIdentityType,
    secondaryIdentityNumber: dataPH?.secondaryIdentityNumber,
    countryResidence: dataPH.countryResidence,
    sourceFund: dataPH?.sourceFund,
    occupation: dataPH?.occupation,
    industry: dataPH.industry,
    attachments: [
      {
        attachmentId: dataPH.passportImageId,
        type: AttachmentId.PassportImage,
      },
      {
        attachmentId: dataPH.signatureImageId,
        type: AttachmentId.SignatureImage,
      },
    ],
  };
};

const handlePersonalDetailByCountry = (data: PersonalDetail) => {
  const countryCode = getCountryType(data);

  const personalDetailByCountry = {
    [CountryCode.Philippines]: handlePersonalDetailsPH,
    [CountryCode.Malaysia]: () => ({}),
  };

  return personalDetailByCountry[countryCode](data);
};
